
import { useState, useEffect } from "react";
import { setExternalPuzzleData } from "@/lib/puzzle/puzzle-operations";
import type { PuzzleData } from "@/lib/types/puzzle-types";
import { extractSpreadsheetId, fetchSpreadsheetAsCSV, findColumnIndices, findRowByDate } from "@/utils/sheetUtils";
import { processRowData, createPuzzleData } from "@/utils/puzzleDataUtils";
import { getTodayString } from "@/utils/dateUtils";
import { fetchPuzzleFromDB, storePuzzleInDB } from "@/lib/database/puzzle-storage";
import { convertStorageToPuzzleData } from "@/lib/puzzle/format-converters";

/**
 * Hook to fetch and process puzzle data from a Google Spreadsheet
 * This is the primary source of puzzle data for the application
 */
export function useExternalPuzzleSource(externalDataUrl?: string) {
  const [externalPuzzle, setExternalPuzzle] = useState<PuzzleData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Create a key that changes with the date to ensure we refresh daily
  const today = getTodayString();

  useEffect(() => {
    if (!externalDataUrl) {
      console.log("No external data URL provided");
      return;
    }

    const loadExternalData = async () => {
      setIsLoading(true);
      setError(null);

      // Temporarily remove cache usage to force fresh data
      const forceRefresh = true; // FORCED REFRESH - ALWAYS FETCH FRESH DATA
      
      if (forceRefresh) {
        console.log("FORCED REFRESH: Bypassing cache and fetching fresh data");
        localStorage.removeItem("puzzle_date");
        localStorage.removeItem("current_puzzle");
        localStorage.removeItem("puzzle_cache_time");
      }
      
      try {
        // Skip database check and go straight to spreadsheet when forcing refresh
        if (forceRefresh) {
          console.log("FORCED REFRESH: Bypassing database check");
        } else {
          // Normal flow - check database first (only runs when not forcing refresh)
          console.log("Checking database for today's puzzle");
          const dbPuzzle = await fetchPuzzleFromDB(today);
          
          if (dbPuzzle) {
            console.log("Found puzzle in database for date:", today);
            
            // Convert storage format to runtime format
            const puzzleData = convertStorageToPuzzleData(dbPuzzle);
            console.log("Database puzzle has word links:", !!puzzleData.wordLinks);
            
            // Cache the database puzzle in localStorage too
            localStorage.setItem("puzzle_date", today);
            localStorage.setItem("current_puzzle", JSON.stringify(puzzleData));
            localStorage.setItem("puzzle_cache_time", Date.now().toString());
            
            setExternalPuzzle(puzzleData);
            setExternalPuzzleData(puzzleData);
            setIsLoading(false);
            return;
          }
        }
        
        // Fetch from spreadsheet
        console.log("FORCED REFRESH: Fetching fresh puzzle data from spreadsheet for date:", today);
        
        // Extract the spreadsheet ID from the URL
        const spreadsheetId = extractSpreadsheetId(externalDataUrl);
        
        console.log('Fetching puzzle data from spreadsheet ID:', spreadsheetId);
        console.log('Looking for today\'s date:', today);
        
        // Fetch spreadsheet data as CSV
        const rows = await fetchSpreadsheetAsCSV(spreadsheetId);
        
        if (!rows || rows.length < 2) {
          throw new Error("Empty or invalid puzzle data");
        }
        
        // Find column indices from headers
        const { dateIndex, wordIndex, ruleIndex, reasoningIndex, spectrumIndex, linkIndices } = findColumnIndices(rows[0]);
        
        console.log('FORCED DEBUG - Found headers:', { 
          dateIndex, 
          wordIndex, 
          ruleIndex, 
          reasoningIndex, 
          spectrumIndex,
          linkIndices,
          hasLinkColumns: linkIndices.length > 0,
          linkIndicesContent: linkIndices,
          headerRow: rows[0],
        });
        
        // Find today's row - use our consistent date format
        const todayRow = findRowByDate(rows, dateIndex, today);
        
        if (!todayRow) {
          throw new Error(`No puzzle available for today (${today})`);
        }
        
        console.log('FORCED DEBUG - Found today\'s row:', todayRow);
        
        // Process the row data
        const { words, ruleName, reasoning, spectrum, wordLinks } = processRowData(
          todayRow, 
          wordIndex, 
          ruleIndex, 
          reasoningIndex, 
          spectrumIndex,
          linkIndices
        );
        
        console.log('FORCED DEBUG - Processed row data:', { 
          ruleName, 
          words, 
          spectrum: spectrum ? spectrum.join(' to ') : 'None',
          hasSpectrum: !!spectrum,
          wordsCount: words.length,
          wordLinks,
          hasWordLinks: !!wordLinks,
          reasoning: reasoning ? (reasoning.substring(0, 50) + '...') : 'No reasoning provided'
        });
        
        // Create the puzzle directly from spreadsheet data
        const puzzle = createPuzzleData(words, ruleName, reasoning, spectrum, wordLinks);
        
        // Explicitly set the date to ensure consistency
        puzzle.date = today;
        
        console.log('FORCED DEBUG - Successfully loaded puzzle from spreadsheet', {
          date: puzzle.date,
          rule: puzzle.correctRule.name,
          ruleId: puzzle.correctRule.id,
          spectrum: spectrum,
          hasSpectrum: !!spectrum,
          words: puzzle.words,
          correctOrder: puzzle.correctOrder,
          wordLinks: puzzle.wordLinks,
          hasWordLinks: !!puzzle.wordLinks,
          wordLinksContent: puzzle.wordLinks,
          reasoning: puzzle.correctRule.reasoning?.substring(0, 50) + '...' || 'No reasoning provided'
        });
        
        // Store the puzzle in database for future use - this is already in the correct storage format
        console.log('Storing puzzle in database for future use');
        await storePuzzleInDB(puzzle);
        
        // Cache the puzzle data with today's date in localStorage
        localStorage.setItem("puzzle_date", today);
        localStorage.setItem("current_puzzle", JSON.stringify(puzzle));
        localStorage.setItem("puzzle_cache_time", Date.now().toString());
        
        // Set both the local state and the global override
        setExternalPuzzle(puzzle);
        setExternalPuzzleData(puzzle);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading puzzle data:', err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsLoading(false);
      }
    };
    
    loadExternalData();
  }, [externalDataUrl, today]);
  
  return {
    externalPuzzle,
    isLoading,
    error
  };
}
